(function () {
  const SKIP_SECTIONS = new Set([
    'preptera-announcement-bar',
    'preptera-breadcrumbs',
    'preptera-article-hero',
    'preptera-article-content',
  ]);
  const DECORATIVE_CLASS_PARTS = ['__bg', '__bg-image', '__panel-bg', '__swirl', '__scroll-hint'];

  const REVEAL_SELECTORS = [
    'header[class*="__header"]',
    '[class*="__headers"]',
    '[class*="__grid"] > *',
    '[class*="__cards"] > *',
    '[class*="__row"] > *',
    '[class*="__bullets"] > *',
    '[class*="__tags"] > *',
    '[class*="__plans"] > *',
    '[class$="__card"]',
    '[class$="__card-shell"]',
    '[class$="__bullet"]',
    '[class$="__plan"]',
    '[class$="__stat"]',
    '.preptera-feature-card',
    '.preptera-blog-card',
    '[class*="__layout"] > *',
    '[class$="__media"]',
    '[class$="__copy"]',
    '[class$="__content"]',
    '[class$="__quote-block"]',
    '[class$="__form-wrap"]',
    '[class$="__form-card"]',
    '[class$="__panel-inner"]',
    '[class$="__phones"]',
    '[class$="__mockup-wrap"]',
    '[class$="__stack"]',
    '[class$="__ctas"]',
    '[class$="__stats"]',
    '.preptera-features-primary-grid [class$="__item"]',
    '.preptera-contact-faq [class$="__item"]',
    '.footer--preptera .footer__preptera-brand-col',
    '.footer--preptera .footer__preptera-menu-col',
    '.footer--preptera .footer__preptera-bottom',
  ].join(',');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const observed = new WeakSet();
  let observer;

  function isDecorative(element) {
    return DECORATIVE_CLASS_PARTS.some((part) => {
      return Array.from(element.classList).some((className) => className.includes(part));
    });
  }

  function shouldSkip(element) {
    if (!element || element.nodeType !== 1) return true;
    if (element.classList.contains('preptera-reveal') || element.classList.contains('pp-reveal')) return false;
    if (element.classList.contains('scroll-trigger')) return true;
    if (isDecorative(element)) return true;
    if (element.closest('[class*="__bg"], [class*="__bg-image"], [class*="__swirl"]')) return true;

    const section = element.closest('section[class*="preptera-"]');
    if (section) {
      const sectionClass = Array.from(section.classList).find((className) => className.startsWith('preptera-'));
      if (sectionClass && SKIP_SECTIONS.has(sectionClass)) return true;
    }

    return false;
  }

  function dedupeTargets(elements) {
    return elements.filter((element) => {
      return !elements.some((other) => other !== element && other.contains(element));
    });
  }

  function collectTargets(root = document) {
    const sections = root.matches?.('section[class*="preptera-"]')
      ? [root]
      : Array.from(root.querySelectorAll('section[class*="preptera-"]'));

    if (root.matches?.('.footer--preptera')) {
      sections.push(root);
    } else if (root.querySelector?.('.footer--preptera')) {
      sections.push(root.querySelector('.footer--preptera'));
    }

    const matches = [];

    sections.forEach((section) => {
      const sectionClass = Array.from(section.classList).find((className) => className.startsWith('preptera-'));
      if (sectionClass && SKIP_SECTIONS.has(sectionClass)) return;

      section.querySelectorAll(REVEAL_SELECTORS).forEach((element) => {
        if (!shouldSkip(element)) matches.push(element);
      });
    });

    return dedupeTargets(matches);
  }

  function applyStaggerDelays(targets) {
    const groups = new Map();

    targets.forEach((target) => {
      const group = target.closest('section[class*="preptera-"], .footer--preptera') || document.body;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(target);
    });

    groups.forEach((groupTargets) => {
      groupTargets.forEach((target, index) => {
        if (target.style.getPropertyValue('--preptera-reveal-delay')) return;
        if (target.style.getPropertyValue('--pp-d')) return;
        target.style.setProperty('--preptera-reveal-delay', `${Math.min(index * 0.08, 0.4)}s`);
      });
    });
  }

  function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > 0 && rect.top < viewportHeight * 0.92;
  }

  function revealElement(element) {
    element.classList.add('is-in');
    observer?.unobserve(element);
  }

  function revealImmediately(elements) {
    elements.forEach((element) => {
      revealElement(element);
    });
  }

  function observeElements(elements) {
    if (prefersReducedMotion || document.documentElement.classList.contains('shopify-design-mode')) {
      revealImmediately(elements);
      return;
    }

    elements.forEach((element) => {
      if (element.classList.contains('is-in')) return;

      if (!element.classList.contains('preptera-reveal') && !element.classList.contains('pp-reveal')) {
        element.classList.add('preptera-reveal');
      }

      if (isInViewport(element)) {
        revealElement(element);
        return;
      }

      if (observed.has(element)) return;

      observed.add(element);
      observer.observe(element);
    });
  }

  function createObserver() {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealElement(entry.target);
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -5% 0px',
      }
    );
  }

  function scan(root = document) {
    document.documentElement.classList.add('preptera-reveal-ready');

    const manualTargets = Array.from(
      root.querySelectorAll?.('.pp-reveal:not(.is-in), .preptera-reveal:not(.is-in)') || []
    );
    const autoTargets = collectTargets(root);
    const targets = dedupeTargets([...manualTargets, ...autoTargets]);

    applyStaggerDelays(targets);
    observeElements(targets);
  }

  function init() {
    if (prefersReducedMotion || document.documentElement.classList.contains('shopify-design-mode')) {
      document.documentElement.classList.add('preptera-reveal-ready');
      document.querySelectorAll('.pp-reveal, .preptera-reveal').forEach((element) => {
        element.classList.add('is-in');
      });
      scan();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.documentElement.classList.add('preptera-reveal-ready');
      scan();
      document.querySelectorAll('.pp-reveal, .preptera-reveal').forEach((element) => {
        element.classList.add('is-in');
      });
      return;
    }

    observer = createObserver();
    scan();
    requestAnimationFrame(() => scan());
  }

  window.prepteraReveal = { scan, refresh: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    scan();
  });
})();
